/**
 * useGroupRequests Hook
 * Standardized API hooks for group request management
 *
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import {
  GroupRequest,
  GroupRequestCreate,
  GroupRequestListResponse,
  GroupRequestApproval,
  User,
} from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createQueryHookNoParams, createMutationHook } from './factories';

// ============================================================================
// Query Keys
// ============================================================================

export const groupRequestQueryKeys = {
  all: ['group-requests'] as const,
  lists: () => [...groupRequestQueryKeys.all, 'list'] as const,
  myRequests: (page?: number, pageSize?: number) => [
    ...groupRequestQueryKeys.lists(),
    'my-requests',
    page,
    pageSize,
  ] as const,
  pendingRequests: (page?: number, pageSize?: number) => [
    ...groupRequestQueryKeys.lists(),
    'pending',
    page,
    pageSize,
  ] as const,
  details: () => [...groupRequestQueryKeys.all, 'detail'] as const,
  detail: (requestId: string) => [...groupRequestQueryKeys.details(), requestId] as const,
  eligibleApprovers: () => [...groupRequestQueryKeys.all, 'eligible-approvers'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get current user's group requests (paginated).
 *
 * Features:
 * - Server-side pagination with page/pageSize
 * - Returns full response with pagination metadata
 * - Short stale time (30s) as request status changes frequently
 *
 * @example
 * const { data } = useMyGroupRequests({ page: 1, pageSize: 50 });
 * const requests = data?.requests || [];
 * const pagination = data?.pagination;
 */
export const useMyGroupRequests = (
  params?: { page?: number; pageSize?: number },
  options?: any
) => {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;

  return createQueryHookNoParams<GroupRequestListResponse>({
    queryKey: () => groupRequestQueryKeys.myRequests(page, pageSize),
    queryFn: (apiClient) =>
      apiClient
        .getMyRequestsApiGroupsRequestsMyRequestsGet({ page, page_size: pageSize })
        .then((response) => response.data),
    logContext: 'GET /api/group-requests/requests/my-requests',
    staleTime: QUERY_STALE_TIME.SHORT, // Request status changes frequently
    onSuccess: (data) => {
      log.api.response('GET', '/api/group-requests/requests/my-requests', 200, {
        requestCount: data?.requests?.length ?? 0,
        total: data?.pagination?.total ?? 0,
      });
    },
  })(options);
};

/**
 * Hook to get pending group requests for moderators/admins (paginated).
 *
 * @example
 * const { data } = usePendingGroupRequests({ page: 1, pageSize: 50 });
 */
export const usePendingGroupRequests = (
  params?: { page?: number; pageSize?: number },
  options?: any
) => {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;

  return createQueryHookNoParams<GroupRequestListResponse>({
    queryKey: () => groupRequestQueryKeys.pendingRequests(page, pageSize),
    queryFn: (apiClient) =>
      apiClient
        .getPendingRequestsApiGroupsRequestsPendingGet({ page, page_size: pageSize })
        .then((response) => response.data),
    logContext: 'GET /api/group-requests/requests/pending',
    staleTime: QUERY_STALE_TIME.SHORT,
    onSuccess: (data) => {
      log.api.response('GET', '/api/group-requests/requests/pending', 200, {
        requestCount: data?.requests?.length ?? 0,
        total: data?.pagination?.total ?? 0,
      });
    },
  })(options);
};

/**
 * Hook to get a specific group request by ID.
 *
 * @example
 * const { data: request } = useGroupRequest('request-123');
 */
export const useGroupRequest = createQueryHook<GroupRequest, string>({
  queryKey: (requestId) => groupRequestQueryKeys.detail(requestId),
  queryFn: (apiClient, requestId) =>
    apiClient
      .getRequestDetailsApiGroupsRequestsRequestIdGet(requestId)
      .then((response) => response.data),
  logContext: 'GET /api/group-requests/requests/:requestId',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data, requestId) => {
    log.api.response('GET', `/api/group-requests/requests/${requestId}`, 200, {
      requestId: data.id,
      status: data.status,
    });
  },
});

/**
 * Hook to get eligible approvers (admin/moderator users).
 *
 * @example
 * const { data: approversResponse } = useEligibleApprovers();
 * const approvers = approversResponse?.approvers || [];
 */
export const useEligibleApprovers = createQueryHookNoParams<{ approvers: User[]; total: number }>({
  queryKey: () => groupRequestQueryKeys.eligibleApprovers(),
  queryFn: (apiClient) =>
    apiClient
      .getEligibleApproversApiGroupsRequestsEligibleApproversGet()
      .then((response) => response.data),
  logContext: 'GET /api/groups/requests/eligible-approvers',
  staleTime: QUERY_STALE_TIME.MEDIUM, // User list doesn't change that often
  onSuccess: (data) => {
    log.api.response('GET', '/api/groups/requests/eligible-approvers', 200, {
      approverCount: data?.approvers?.length ?? 0,
    });
  },
});

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new group request.
 *
 * @example
 * const createRequest = useCreateGroupRequest();
 *
 * await createRequest.mutateAsync({
 *   group_name: 'Engineering Team',
 *   description: 'Team for engineering department',
 *   justification: 'Need a dedicated team for collaboration',
 *   group_type: 'team',
 *   visibility: 'organization',
 *   approver_ids: ['admin-user-id'],
 *   initial_admin_id: 'other-user-id',
 * });
 */
export const useCreateGroupRequest = createMutationHook<GroupRequest, GroupRequestCreate>({
  mutationFn: (apiClient, requestData) =>
    apiClient
      .createGroupRequestApiGroupsRequestsPost(requestData)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all, ['groups']],
  logContext: 'POST /api/group-requests/requests',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('group_request_created', 'useCreateGroupRequest', {
      requestId: data.id,
      groupName: data.group_name,
    });
  },
});

/**
 * Hook to approve a group request.
 *
 * @example
 * const approveRequest = useApproveGroupRequest();
 *
 * await approveRequest.mutateAsync({
 *   requestId: 'request-123',
 *   approval: { admin_notes: 'Approved for engineering team' }
 * });
 */
export const useApproveGroupRequest = createMutationHook<
  GroupRequest,
  { requestId: string; approval: GroupRequestApproval }
>({
  mutationFn: (apiClient, { requestId, approval }) =>
    apiClient
      .approveGroupRequestApiGroupsRequestsRequestIdApprovePost(requestId, approval)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all, ['groups']],
  logContext: 'POST /api/group-requests/requests/:requestId/approve',
  logMethod: 'POST',
  onSuccess: (data, variables) => {
    log.user.action('group_request_approved', 'useApproveGroupRequest', {
      requestId: variables.requestId,
      groupName: data.group_name,
      createdGroupId: data.created_group_id,
    });
  },
});

/**
 * Hook to reject a group request.
 *
 * @example
 * const rejectRequest = useRejectGroupRequest();
 *
 * await rejectRequest.mutateAsync({
 *   requestId: 'request-123',
 *   rejection: {
 *     rejection_reason: 'Group already exists with similar purpose',
 *     admin_notes: 'Please join existing team instead'
 *   }
 * });
 */
export const useRejectGroupRequest = createMutationHook<
  GroupRequest,
  { requestId: string; rejection: GroupRequestApproval }
>({
  mutationFn: (apiClient, { requestId, rejection }) =>
    apiClient
      .rejectGroupRequestApiGroupsRequestsRequestIdRejectPost(requestId, rejection)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all],
  logContext: 'POST /api/group-requests/requests/:requestId/reject',
  logMethod: 'POST',
  onSuccess: (data, variables) => {
    log.user.action('group_request_rejected', 'useRejectGroupRequest', {
      requestId: variables.requestId,
      groupName: data.group_name,
    });
  },
});

/**
 * Hook to cancel a group request (requester only).
 *
 * @example
 * const cancelRequest = useCancelGroupRequest();
 *
 * await cancelRequest.mutateAsync('request-123');
 */
export const useCancelGroupRequest = createMutationHook<GroupRequest, string>({
  mutationFn: (apiClient, requestId) =>
    apiClient
      .cancelGroupRequestApiGroupsRequestsRequestIdDelete(requestId)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all],
  logContext: 'DELETE /api/group-requests/requests/:requestId',
  logMethod: 'DELETE',
  onSuccess: (data, requestId) => {
    log.user.action('group_request_cancelled', 'useCancelGroupRequest', {
      requestId,
      groupName: data.group_name,
    });
  },
});

/**
 * Hook to mark a request as being viewed (5-minute lock).
 *
 * @example
 * const markViewing = useMarkRequestAsViewing();
 *
 * await markViewing.mutateAsync('request-123');
 */
export const useMarkRequestAsViewing = createMutationHook<GroupRequest, string>({
  mutationFn: (apiClient, requestId) =>
    apiClient
      .markRequestViewingApiGroupsRequestsRequestIdMarkViewingPost(requestId)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all],
  logContext: 'POST /api/group-requests/requests/:requestId/mark-viewing',
  logMethod: 'POST',
  onSuccess: (_data, requestId) => {
    log.user.action('group_request_viewing_marked', 'useMarkRequestAsViewing', {
      requestId,
    });
  },
});

/**
 * Hook to clear viewing status on a request.
 *
 * @example
 * const clearViewing = useClearRequestViewing();
 *
 * await clearViewing.mutateAsync('request-123');
 */
export const useClearRequestViewing = createMutationHook<GroupRequest, string>({
  mutationFn: (apiClient, requestId) =>
    apiClient
      .clearRequestViewingApiGroupsRequestsRequestIdClearViewingPost(requestId)
      .then((response) => response.data),
  invalidateQueries: [groupRequestQueryKeys.all],
  logContext: 'POST /api/group-requests/requests/:requestId/clear-viewing',
  logMethod: 'POST',
  onSuccess: (_data, requestId) => {
    log.user.action('group_request_viewing_cleared', 'useClearRequestViewing', {
      requestId,
    });
  },
});
